import { includes, isInteger, unionWith, forEach, get, orderBy, findKey, map, omit, set } from 'lodash-es';

import { isDateAfter, isDateValid, isEmail, isPhone } from '../../Utils/Utils';

/**
 * getWarningMessage
 * Get warning message template
 * @param {string} txt Mapping key to locale string or text
 * @param {object} strings Locale strings
 * @return {object} { type: string, message: string }
 */
const getWarningMessage = (txt, strings) => ({
  type: 'warning',
  message: get(strings, txt, txt),
});

const getTransformParty = ({ transform, covered }) => {
  try {
    if (transform) {
      if (transform.party) return transform.party;
      if (transform.subscriber >= 0 && Array.isArray(covered) && covered[transform.subscriber]) {
        return covered[transform.subscriber].party;
      }
    }
  } catch (e) {
    return undefined;
  }
};

const getSubscriptionsParties = ({ subscriptions = [], covered }) => {
  try {
    const suscriptionsParties = subscriptions.map(subscription => {
      // if subscriber is a covered
      const subscriber = get(subscription, 'subscriber', undefined);
      if (typeof subscriber !== 'undefined' && subscriber >= 0) return get(covered, `[${subscriber}].party`);
      // else
      return get(subscription, 'party');
    });
    return suscriptionsParties;
  } catch (e) {
    return undefined;
  }
};

const getActions = status => {
  switch (status) {
    case 'submitted':
    case 'canceled': {
      return [
        {
          route: '/quotations/',
          action: 'view',
          type: 'link',
          label: 'quotations.grid.cell.actions.btn.view.label',
        },
      ];
    }
    case 'pending':
    case 'project': {
      return [
        {
          route: '/quotations/',
          action: 'modify',
          type: 'link',
          label: 'quotations.grid.cell.actions.btn.modify.label',
        },
        {
          route: 'cancel',
          type: 'action',
          action: 'cancel',
          label: 'quotations.grid.cell.actions.btn.cancel.label',
        },
      ];
    }
    default: {
      return [];
    }
  }
};

export const getFullQuotationItem = quotation => {
  const fullQuotation = quotation;
  const isLoan = findKey(quotation, { loan: true }) || false;

  return { ...fullQuotation, isLoan };
};

export const makeCompatibleWithAPIv1 = ({ quotation }) => {
  let compatibleQuotation = quotation;

  if (get(compatibleQuotation, 'transform.subscriber') === -1) {
    compatibleQuotation = {
      ...compatibleQuotation,
      transform: omit(get(compatibleQuotation, 'transform'), 'subscriber'),
    };
  }
  if (
    typeof get(compatibleQuotation, 'transform.party.name') === 'undefined' &&
    get(compatibleQuotation, 'transform.party.is_person')
  ) {
    compatibleQuotation = { ...compatibleQuotation, transform: omit(get(compatibleQuotation, 'transform'), 'party') };
  }
  // ===
  return compatibleQuotation;
};

export const makeCompatibleWithSR = quotation => {
  const compatibleQuotation = { ...quotation, agent: { id: quotation.agent }, broker: { id: quotation.broker } };

  return compatibleQuotation;
};

export const getQuotationItem = item => {
  const quotation = makeCompatibleWithAPIv1({ quotation: item });
  const party = getTransformParty(quotation);
  const parties = getSubscriptionsParties(quotation);
  return {
    id: quotation.id,
    createdOn: get(quotation, 'createdOn'),
    writeDate: get(quotation, 'writeDate'),
    date: get(quotation, 'date'),
    contractId: get(quotation, 'contract.id'),
    contractQuoteNumber: get(quotation, 'contract.quote_number'),
    commercialProductId: get(quotation, 'commercial_product.id'),
    commercialProductName: get(quotation, 'commercial_product.name'),
    productId: get(quotation, 'product.id'),
    product: get(quotation, 'product'),
    party,
    parties,
    partyName: `${get(party, 'first_name', '')} ${get(party, 'name', '')}`,
    partyId: get(party, 'code'),
    status: get(quotation, 'quote_status'),
    actions: getActions(get(quotation, 'quote_status')),
  };
};

export const getQuotationList = quotations => {
  try {
    return quotations.map(quotation => {const qt = getQuotationItem(quotation)
      return qt
    });
  } catch (e) {
    return [];
  }
};

// eslint-disable-next-line no-unused-vars
export const updateQuotationList = (quotationList, quotationItem) => {
  try {
    const item = getQuotationItem(quotationItem);
    // Find item index using _.findIndex (thanks @AJ Richardson for comment)
    return unionWith([item], quotationList, (l, r) => l.id === r.id);
  } catch (e) {
    return quotationList;
  }
};

export const getSortedExtraData = item => {
  const covered = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(item)) {
    let extra = get(value, 'extra');

    extra = orderBy(extra, ['sequence'], ['asc']); // Use Lodash to sort array by 'name'

    covered[key] = { ...value, extra };
  }
  return covered;
};

export const getCalc = item => {
  const calc = item;
  const contract = get(item, 'contract', {});
  const covered = getSortedExtraData(get(contract, 'covered', {}));

  return { ...calc, contract: { ...contract, covered } };
};

export const getQuotation = item => {
  const quotation = {
    ...item,
    calc: getCalc(get(item, 'calc')),
  };

  return quotation;
};

/**
 * isDatesValid
 * Are dates valid and if release date exist, it should be after date
 * @param {object} quotation { date: Date, release_date: Date}
 * @return {object} { dateValid: boolean, releaseDateValid: boolean}
 */
const isDatesValid = ({ date, release_date: releaseDate, covered = [] }) => {
  const isQuotDateValid = isDateValid(date) && isDateAfter(date);
  const isReleaseDateValid = isDateValid(releaseDate) && isDateAfter(releaseDate, date);
  let isBirthDateValid = true;

  forEach(covered, cov => {
    isBirthDateValid = isBirthDateValid && isDateValid(cov?.party?.birth_date);
  });

  const isValid = {
    dateValid: isQuotDateValid,
    releaseDateValid: releaseDate ? isQuotDateValid && isReleaseDateValid : true,
    birthDateValid: isBirthDateValid,
  };

  return isValid;
};

/**
 * isCoveredValid
 * Are covered fields valid, such as first name, email, phone and share
 * @param {object} quotation { covered: [{ party: { first_name: string, phone: number, email: string }, share: number }] }
 * @return {object} { firstNameExist: boolean, phoneValid: boolean, emailValid: boolean, shareValid: boolean }
 */
const isCoveredValid = ({ covered } = {}) => {
  if (!covered || !covered.length) {
    return { firstNameExist: false, phoneValid: false, emailValid: false, shareValid: false };
  }

  const isValid = covered.reduce(
    (acc, { party, share }) => {
      if (!acc.firstNameExist || !acc.phoneValid || !acc.emailValid || !acc.shareValid || !party) return acc;
      return {
        firstNameExist: get(party, 'first_name', false),
        phoneValid: isPhone(party?.phone),
        emailValid: isEmail(party?.email),
        shareValid: share === undefined || (isInteger(share) && share >= 1 && share <= 100),
      };
    },
    { firstNameExist: true, phoneValid: true, emailValid: true, shareValid: true }
  );

  return isValid;
};

/**
 * isSubscriptionsValid
 * Are subscriptions fields valid, such as first name, email and phone
 * @param {object} quotation { subscriptions: [{ party: { first_name: string }, phone: number, email: string }] }
 * @return {object} { firstNameExist: boolean, phoneValid: boolean, emailValid: boolean, }
 */
const isSubscriptionsValid = ({ subscriptions } = {}) => {
  const isValid = subscriptions.reduce(
    (acc, sub) => {
      if (!sub) return acc;
      const { subscriber, email, phone, party } = sub;
      if (subscriber == null || subscriber >= 0 || !acc.firstNameExist || !acc.phoneValid || !acc.emailValid) {
        return acc;
      }
      return {
        firstNameExist: get(party, 'is_person') ? !!get(party, 'first_name') : true,
        phoneValid: isPhone(phone),
        emailValid: isEmail(email),
      };
    },
    { firstNameExist: true, phoneValid: true, emailValid: true }
  );

  return isValid;
};

/**
 * isTransformValid
 * Are transform fields valid, such as first name, email and phone
 * @param {object} quotation { transform: { party: { first_name: string }, phone: number, email: string } }
 * @return {object} { firstNameExist: boolean, phoneValid: boolean, emailValid: boolean }
 */
const isTransformValid = ({ transform } = {}) => {
  const firstNameExist = get(transform, 'party.is_person') ? get(transform, 'party.first_name', false) : true;
  const phoneValid = isPhone(transform?.phone);
  const emailValid = isEmail(transform?.email);

  return { firstNameExist, phoneValid, emailValid };
};

/**
 * getMsgLists
 * Get error/warning messages for each invalid field
 * @param {object} isFieldsValid Object of boolean for each field
 * @param {array} msgQuot List of previous messages for quotation
 * @param {array} msgSubscr List of previous messages for subscription
 * @param {boolean} addToQuot If message is added to quotation's messages
 * @param {boolean} addToSubscr If message is added to subscription's messages
 * @param {object} strings Locale strings
 * @return {object} { msgQuot: [{ type: string, message: string }], msgSubscr: [{ type: string, message: string }] }
 */
const getMsgLists = ({ isFieldsValid, msgQuot, msgSubscr, addToQuot = false, addToSubscr = false, strings }) => {
  forEach(isFieldsValid, (value, key) => {
    if (!value) {
      let msg;

      switch (key) {
        case 'dateValid':
          msg = getWarningMessage('validation.message.invalid_date', strings);
          break;
        case 'releaseDateValid':
          msg = getWarningMessage('validation.message.invalid_release_date', strings);
          break;
        case 'birthDateValid':
          msg = getWarningMessage('validation.message.invalid_birthDate', strings);
          break;
        case 'firstNameExist':
          msg = getWarningMessage('validation.message.missing_firstname', strings);
          break;
        case 'emailValid':
          msg = getWarningMessage('validation.message.invalid_email', strings);
          break;
        case 'phoneValid':
          msg = getWarningMessage('validation.message.invalid_phone', strings);
          break;
        case 'shareValid':
          msg = getWarningMessage('validation.message.invalid_share', strings);
          break;
        default:
          break;
      }

      if (addToQuot) msgQuot.push(msg);
      if (addToSubscr) msgSubscr.push(msg);
    }
  });

  return { msgQuot, msgSubscr };
};

/**
 * reduceMessagesFields
 * Reducer for a field to get error/warning messages
 * @param {object} quotation Quotation
 * @param {string} field Field to validate
 * @param {array} msgQuot List of previous messages for quotation
 * @param {array} msgSubscr List of previous messages for subscription
 * @param {object} strings Locale strings
 * @return {object} { msgQuot: [{ type: string, message: string }], msgSubscr: [{ type: string, message: string }] }
 */
const reduceMessagesFields = ({ quotation, field, msgQuot, msgSubscr, strings }) => {
  const { subscriptions, transform } = quotation;
  let isFieldsValid;
  let addToQuot = false;
  let addToSubscr = false;

  switch (field) {
    case 'date':
      isFieldsValid = isDatesValid(quotation);
      addToQuot = true;
      addToSubscr = true;
      break;
    case 'covered':
      isFieldsValid = isCoveredValid(quotation);
      addToQuot = true;
      addToSubscr = true;
      break;
    case 'subscriptions':
      if (!subscriptions || !subscriptions.length) {
        return { msgQuot, msgSubscr };
      }

      isFieldsValid = isSubscriptionsValid(quotation);
      addToSubscr = true;
      break;
    case 'transform':
      if (!transform || transform.subscriber !== -1 || !transform.party) {
        return { msgQuot, msgSubscr };
      }

      isFieldsValid = isTransformValid(quotation);
      addToSubscr = true;
      break;
    default:
      break;
  }

  const messages = getMsgLists({
    isFieldsValid,
    msgQuot,
    msgSubscr,
    addToQuot,
    addToSubscr,
    strings,
  });

  return messages;
};

/**
 * getMessagesFields
 * Validate each fields and get error/warning messages
 * @param {object} quotation Quotation
 * @param {array} fields Fields' list to validate
 * @param {object} strings Locale strings
 * @return {object} { msgQuot: [{ type: string, message: string }], msgSubscr: [{ type: string, message: string }] }
 */
const getMessagesFields = ({ quotation, fields = [], strings }) =>
  fields.reduce(
    (acc, field) => reduceMessagesFields({ quotation, field, msgQuot: acc.msgQuot, msgSubscr: acc.msgSubscr, strings }),
    { msgQuot: [], msgSubscr: [] }
  );

/**
 * validateFields
 * Validate fields
 * @param {object} quotation Quotation
 * @param {object} strings Locale strings
 * @return {object} { quotation: { valid: boolean, messages: array }, print: { valid: boolean, messages: array }, subscription: { valid: boolean, messages: array } }
 */
const validateFields = (quotation = {}, strings) => {
  const status = get(quotation, `quote_status`, 'default');

  if (!['submitted', 'canceled'].includes(status)) {
    const fields = ['date', 'covered', 'subscriptions', 'transform'];
    const { msgQuot, msgSubscr } = getMessagesFields({ quotation, fields, strings });

    const data = {
      quotation: { valid: !msgQuot.length, messages: msgQuot },
      print: { valid: !msgQuot.length, messages: msgQuot },
      subscription: { valid: !msgSubscr.length, messages: msgSubscr },
    };

    return data;
  }

  return null;
};

export const getValidateFront = (quotation, data, strings) => {
  const validationFields = validateFields(quotation, strings);
  const newData = data;

  if (validationFields) {
    newData.quotation.valid = get(newData, 'quotation.valid') && get(validationFields, 'quotation.valid');
    newData.quotation.messages = {
      ...get(newData, 'quotation.messages'),
      ...get(validationFields, 'quotation.messages'),
    };
    newData.print.valid = get(newData, 'print.valid') && get(validationFields, 'print.valid');
    newData.print.messages = { ...get(newData, 'print.messages'), ...get(validationFields, 'print.messages') };
    newData.subscription.valid = get(newData, 'subscription.valid') && get(validationFields, 'subscription.valid');
    newData.subscription.messages = {
      ...get(newData, 'subscription.messages'),
      ...get(validationFields, 'subscription.messages'),
    };
  }

  return newData;
};

export const getQuotationWithProduct = (quot, state) => {
  const stateProduct = state.getIn(['products', get(quot, 'product')]);
  const productObj = stateProduct ? stateProduct.toJS() : null;

  if (productObj && productObj.loan_configuration) {
    set(productObj, 'item_descriptors[0].single_covered', false);
  }

  return {
    ...quot,
    productObj,
  };
};

/**
 * Add field with value to object if value is not null or undefined
 * @param {object} obj Object to add field to
 * @param {string} name Field's name
 * @param {any} value Field's value
 * @return {object} Object with field and value added
 */
const addFieldIfExist = (obj, name, value) => {
  const newObj = { ...obj };

  if (value !== null && value !== undefined) newObj[name] = value;

  return newObj;
};

/**
 * Add fields with value to object if value is not null or undefined
 * @param {object} obj Object to add fields to
 * @param {array} listFields Array of objects with key name and value for each field to add
 * @return {object} Object with fields and value added
 */
export const addFieldsToObject = (obj, listFields) => {
  let newObj = { ...obj };

  listFields.forEach(({ name, value }) => {
    newObj = addFieldIfExist(newObj, name, value);
  });

  return newObj;
};

export const getLoanKinds = product => {
  const loanKinds = get(product, 'loan_kinds', []).map(kind => ({
    value: kind.code,
    text: kind.name,
  }));

  return loanKinds;
};

export const getAlerts = response => {
  if (response.data.map) {
    return response.data.map(err => `[${response.status}] ${err}`);
  } else if (response.data.errors) {
    return response.data.errors.map(err => `[${response.status}] ${get(err, 'message', err)}`);
  } else if (response.data.error?.message) {
    return [`[${response.status}] ${response.data.error.message}`];
  } else if (response.data.error) {
    return response.data.error.map(err => `[${response.status}] ${get(err, 'message', err)}`);
  } else if (response.data.message) {
    return [`[${response.status}] ${response.data.message}`];
  }

  return [response.status];
};

export const formatValidateMessage = (validate, strings) => {
  const formattedValidate = {};

  forEach(validate.data, (data, path) => {
    const formattedMsgs = map(data.messages, message => {
      if (includes(message.message, 'Une erreur de type "json_schema" a été détectée')) {
        if (includes(message.message, 'birth_date must match pattern ^[0-9]{4}-[0-9]{2}-[0-9]{2}$')) {
          return {
            ...message,
            message: get(strings, 'validation.message.invalid_birthDate', 'Date de naissance non valide'),
          };
        }
      }

      return message;
    });

    formattedValidate[path] = { ...data, messages: formattedMsgs };
  });

  return { ...validate, data: formattedValidate };
};
